import React, { useState } from 'react';
import { Button, Input, Modal, Popconfirm, Space, Table, Tag, Tooltip } from 'antd';
import type { TableColumnsType } from 'antd';
import {
  ApartmentOutlined,
  ArrowDownOutlined,
  ArrowUpOutlined,
  EditOutlined,
  InboxOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useFeedback } from '../../utils/feedback';
import { getApiErrorMessage } from '../../utils/apiError';
import { ReportSection } from '../reports/ReportTab';
import { ObjectCardEmpty } from '../../components/layout/ObjectCard';
import {
  createCompanyCostGroup,
  reorderCompanyCostGroups,
  updateCompanyCostGroup,
  type CompanyCostGroup,
} from './api/companyCostGroupsApi';

// „Grupy kosztów” view of „Koszty stałe” (decision 2026-10-29-grupy-kosztow-i-budzet):
// the owner's own groups (MPK) for the plan and the plan-vs-actual report.
// A group is archived, never deleted — its costs and plans keep pointing at it.

const GROUP_ERRORS = {
  COMPANY_COST_GROUP_NAME_TAKEN: 'fixedCosts:groups.nameTaken',
  COMPANY_COST_GROUP_NAME_REQUIRED: 'fixedCosts:groups.nameRequired',
  COMPANY_COST_GROUP_NAME_TOO_LONG: 'fixedCosts:groups.nameTooLong',
};

interface Props {
  groups: CompanyCostGroup[];
  loading: boolean;
  /** The view switch of „Koszty stałe” (first in the controls row). */
  viewSwitch: React.ReactNode;
  onChanged: () => Promise<void> | void;
}

const CostGroupsView: React.FC<Props> = ({ groups, loading, viewSwitch, onChanged }) => {
  const { t } = useTranslation(['fixedCosts', 'common']);
  const feedback = useFeedback();
  const [editing, setEditing] = useState<CompanyCostGroup | 'new' | null>(null);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  const active = groups.filter((group) => group.isActive);
  const archived = groups.filter((group) => !group.isActive);

  const openNew = () => { setEditing('new'); setName(''); };
  const openRename = (group: CompanyCostGroup) => { setEditing(group); setName(group.name); };

  async function save() {
    const trimmed = name.trim();
    if (!trimmed) {
      feedback.error(t('fixedCosts:groups.nameRequired'));
      return;
    }
    setSaving(true);
    try {
      if (editing === 'new') {
        await createCompanyCostGroup(trimmed);
        feedback.success(t('fixedCosts:groups.created', { name: trimmed }));
      } else if (editing) {
        await updateCompanyCostGroup(editing.id, { name: trimmed });
        feedback.success(t('fixedCosts:groups.renamed', { name: trimmed }));
      }
      setEditing(null);
      await onChanged();
    } catch (error) {
      feedback.error(getApiErrorMessage(error, t, 'fixedCosts:groups.saveFailed', GROUP_ERRORS, { name: trimmed }));
    } finally {
      setSaving(false);
    }
  }

  async function setActive(group: CompanyCostGroup, isActive: boolean) {
    setBusyId(group.id);
    try {
      await updateCompanyCostGroup(group.id, { isActive });
      feedback.success(t(isActive ? 'fixedCosts:groups.restored' : 'fixedCosts:groups.archived', { name: group.name }));
      await onChanged();
    } catch (error) {
      feedback.error(getApiErrorMessage(error, t, 'fixedCosts:groups.saveFailed', GROUP_ERRORS, { name: group.name }));
    } finally {
      setBusyId(null);
    }
  }

  async function move(group: CompanyCostGroup, step: -1 | 1) {
    const ids = active.map((item) => item.id);
    const index = ids.indexOf(group.id);
    const target = index + step;
    if (target < 0 || target >= ids.length) return;
    [ids[index], ids[target]] = [ids[target], ids[index]];
    setBusyId(group.id);
    try {
      await reorderCompanyCostGroups(ids);
      await onChanged();
    } catch (error) {
      feedback.error(getApiErrorMessage(error, t, 'fixedCosts:groups.saveFailed', GROUP_ERRORS, { name: group.name }));
    } finally {
      setBusyId(null);
    }
  }

  const usage = (group: CompanyCostGroup) => t('fixedCosts:groups.usage', {
    templates: group.templateCount ?? 0,
    entries: group.entryCount ?? 0,
  });

  const columns: TableColumnsType<CompanyCostGroup> = [
    {
      title: t('fixedCosts:groups.name'),
      dataIndex: 'name',
      key: 'name',
      render: (value: string, group) => (
        <span className="fixedcosts-table-category">
          <span className="report-cut fixedcosts-name" title={value}>{value}</span>
          {!group.isActive && <Tag>{t('fixedCosts:groups.archivedTag')}</Tag>}
        </span>
      ),
    },
    {
      title: t('fixedCosts:groups.usageTitle'),
      key: 'usage',
      width: 260,
      render: (_value, group) => <span className="report-muted report-cut" title={usage(group)}>{usage(group)}</span>,
    },
    {
      title: t('fixedCosts:table.actions'),
      key: 'actions',
      width: 210,
      align: 'right',
      render: (_value, group) => {
        if (!group.isActive) {
          return (
            <Button size="small" icon={<UndoOutlined />} loading={busyId === group.id} onClick={() => void setActive(group, true)}>
              {t('fixedCosts:groups.restore')}
            </Button>
          );
        }
        const index = active.findIndex((item) => item.id === group.id);
        return (
          <Space>
            <Tooltip title={t('fixedCosts:groups.moveUp')}>
              <Button size="small" icon={<ArrowUpOutlined />} disabled={index === 0} loading={busyId === group.id} onClick={() => void move(group, -1)} aria-label={t('fixedCosts:groups.moveUp')} />
            </Tooltip>
            <Tooltip title={t('fixedCosts:groups.moveDown')}>
              <Button size="small" icon={<ArrowDownOutlined />} disabled={index === active.length - 1} onClick={() => void move(group, 1)} aria-label={t('fixedCosts:groups.moveDown')} />
            </Tooltip>
            <Tooltip title={t('fixedCosts:groups.rename')}>
              <Button size="small" icon={<EditOutlined />} onClick={() => openRename(group)} aria-label={t('fixedCosts:groups.rename')} />
            </Tooltip>
            <Popconfirm
              title={t('fixedCosts:groups.archiveConfirm', { name: group.name })}
              description={t('fixedCosts:groups.archiveConfirmDescription')}
              okText={t('fixedCosts:groups.archive')}
              cancelText={t('common:cancel')}
              onConfirm={() => void setActive(group, false)}
            >
              <Tooltip title={t('fixedCosts:groups.archive')}>
                <Button size="small" icon={<InboxOutlined />} aria-label={t('fixedCosts:groups.archive')} />
              </Tooltip>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <div className="report-tab fixedcosts-page" data-testid="fixedcosts-groups">
      <div className="report-controls">
        {viewSwitch}
        <div className="report-controls__end">
          <span className="report-controls__counter">{t('fixedCosts:groups.counter', { count: active.length })}</span>
          <Button type="primary" icon={<PlusOutlined />} onClick={openNew} data-testid="fixedcosts-group-add">
            {t('fixedCosts:groups.add')}
          </Button>
        </div>
      </div>

      <ReportSection
        icon={<ApartmentOutlined />}
        tone="indigo"
        title={t('fixedCosts:groups.title')}
        count={active.length}
        lead={t('fixedCosts:groups.lead')}
        actions={(
          <Tooltip title={t('fixedCosts:groups.info')} placement="topRight">
            <InfoCircleOutlined className="fixedcosts-info" aria-label={t('fixedCosts:groups.info')} />
          </Tooltip>
        )}
      >
        <Table<CompanyCostGroup>
          className="report-table"
          columns={columns}
          dataSource={showArchived ? [...active, ...archived] : active}
          rowKey="id"
          rowClassName={(group) => (group.isActive ? 'fixedcosts-row' : 'fixedcosts-row fixedcosts-row--inactive')}
          loading={loading}
          size="middle"
          tableLayout="fixed"
          pagination={false}
          locale={{
            emptyText: (
              <ObjectCardEmpty
                icon={<ApartmentOutlined />}
                text={t('fixedCosts:groups.empty')}
                action={<Button icon={<PlusOutlined />} onClick={openNew}>{t('fixedCosts:groups.add')}</Button>}
              />
            ),
          }}
        />
        {archived.length > 0 && (
          <Button type="link" size="small" className="fixedcosts-groups__archived" onClick={() => setShowArchived((value) => !value)}>
            {showArchived ? t('fixedCosts:groups.hideArchived') : t('fixedCosts:groups.showArchived', { count: archived.length })}
          </Button>
        )}
      </ReportSection>

      <Modal
        title={editing === 'new' ? t('fixedCosts:groups.addTitle') : t('fixedCosts:groups.renameTitle')}
        open={editing !== null}
        onCancel={() => !saving && setEditing(null)}
        onOk={() => void save()}
        okText={t('common:save')}
        cancelText={t('common:cancel')}
        confirmLoading={saving}
        destroyOnClose
        width={440}
        className="obj-drawer"
      >
        <Input
          autoFocus
          value={name}
          maxLength={100}
          onChange={(event) => setName(event.target.value)}
          onPressEnter={() => void save()}
          placeholder={t('fixedCosts:groups.namePlaceholder')}
          aria-label={t('fixedCosts:groups.name')}
          data-testid="fixedcosts-group-name"
        />
      </Modal>
    </div>
  );
};

export default CostGroupsView;
